import {
  Avatar,
  Button,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { CircleCloseIcon } from 'Components/CircleCloseIcon';
import { format } from 'date-fns';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import {
  LeftArrow,
  LeftArrowWhite,
  PurpleWarning,
  SuccessIcon,
  VIconCircle,
} from 'Images';
import { ContactUsSegment } from 'Modules/Equipment/EquipmentMain/EquipmentNotAllowed/ContactUsSegment';
import { useNavigate } from 'react-router-dom';

interface ISuccessOrderDialog {
  open: boolean;
  handleClose: () => void;
  renewPrescription?: string[];
  numberOfItems: number;
  linkLabel?: string;
  link?: string;
}
const maxItemsPerOrder = 30;
export const SuccessOrderDialog = (props: ISuccessOrderDialog) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.Equipment,
  ).successOrderDialog;
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const listIcon = (
    <Avatar
      alt=''
      src={VIconCircle}
      sx={{
        height: '20px',
        width: '20px',
        ml: '10px',
      }}
    />
  );
  return (
    <Dialog
      PaperProps={{
        sx: {
          overflowY: 'visible',
          borderRadius: '24px',
          mt: isDesktop ? '44px' : '90px',
          backgroundColor: 'primary.shade_100',
          p: 0,
          ml: 2,
          mr: 2,
        },
      }}
      sx={{
        margin: 'auto',
        maxWidth: '1200px',
        height: isDesktop ? 'fit-content' : undefined,
        '.MuiDialogContent-root': {
          p: isDesktop ? '264px 273px 285pxs' : undefined,
        },
      }}
      maxWidth='md'
      fullWidth={isDesktop}
      open={props.open}
      onClose={props.link ? () => navigate(`${props.link}`) : props.handleClose}
      aria-labelledby={'success-order-dialog-title'}
      aria-describedby={'success-order-dialog-content'}
    >
      <IconButton
        onClick={
          props.link ? () => navigate(`${props.link}`) : props.handleClose
        }
        sx={{
          position: 'absolute',
          left: isDesktop ? '24px' : '13px',
          top: isDesktop ? '24px' : '13px',
          zIndex: 1,
        }}
      >
        <CircleCloseIcon
          alt={generalDictionary.closeWindow}
          sx={{
            width: isDesktop ? '42px' : '34px',
            height: isDesktop ? '42px' : '34px',
          }}
        />
      </IconButton>
      <DialogContent
        sx={{
          maxHeight: isDesktop ? '90vh' : '84vh',
        }}
        id='success-order-dialog-content'
      >
        <Stack
          sx={{
            alignItems: 'center',
            mt: isDesktop ? 5 : '0',
            p: 0,
          }}
        >
          <Avatar
            alt=''
            src={SuccessIcon}
            sx={{
              height: isDesktop ? '92px' : '75px',
              width: isDesktop ? '142px' : '110px',
              m: '0 auto',
              mb: '16px',
            }}
          />
          <Typography
            id='success-order-dialog-title'
            variant='h3'
            sx={{
              textAlign: 'center',
              m: isDesktop ? '0 0 12px' : '0 10px 10px',
              lineHeight: isDesktop ? undefined : '28px',
            }}
          >
            {dictionary.title}
          </Typography>
          {isDesktop ? (
            <Stack
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                m: isDesktop ? '0 0 32px' : '5px 0 21px',
              }}
            >
              <Typography variant='body3'>
                {dictionary.followOrderHere[0]}
              </Typography>
              <Link
                underline='none'
                href={props.link || '/equipment'}
                variant='body3'
              >
                {props.linkLabel || dictionary.followOrderHere[1]}
              </Link>
              <Avatar
                alt=''
                src={LeftArrow}
                sx={{ width: '21px', height: '21px', top: '2px', right: '4px' }}
              />
            </Stack>
          ) : (
            <Stack
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                m: isDesktop ? '0 0 40px' : '5px 0 21px',
              }}
            >
              <Typography variant='body3'>
                {dictionary.followOrderHereMobile[0]}
              </Typography>
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <Button
                  endIcon={
                    <Avatar
                      src={LeftArrow}
                      sx={{
                        width: '18px',
                        height: '18px',
                        top: '2px',
                        right: '4px',
                      }}
                    />
                  }
                  href={props.link || '/equipment'}
                  sx={{ fontSize: '18px', p: 0 }}
                >
                  {props.linkLabel || dictionary.followOrderHereMobile[2]}
                </Button>
              </Stack>
            </Stack>
          )}
          <Typography
            component='h3'
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              m: isDesktop ? '0 0 8px' : ' 0px',
              marginBottom: '16px',
            }}
          >
            {dictionary.date} {format(new Date(Date.now()), 'dd.MM.yy')} |{' '}
            {format(new Date(Date.now()), 'HH:mm')}
          </Typography>
          <Typography
            variant='body3'
            component='h4'
            sx={{
              textAlign: 'center',
              fontWeight: 400,
              mb: '16px',
              marginBottom: '16px',
            }}
          >
            {dictionary.youOrder} {props.numberOfItems} {dictionary.items}
          </Typography>
          {props.numberOfItems >= maxItemsPerOrder && (
            <Stack
              sx={{
                flexDirection: 'row',
                alignItems: isDesktop ? 'center' : 'flex-start',
                justifyContent: 'center',
                gap: '8px',
                mb: isDesktop ? '32px' : '16px',
              }}
            >
              <Avatar
                src={PurpleWarning}
                alt=''
                sx={{
                  width: '22px',
                  height: '22px',
                  mt: isDesktop ? undefined : '3px',
                }}
              />
              <Typography>{dictionary.over30Items}</Typography>
            </Stack>
          )}
          <Container
            disableGutters
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              maxWidth: '550px !important',
              border: '1px solid',
              borderColor: 'primary.shade_200',
              padding: isDesktop ? undefined : '0 16px',
            }}
          >
            <Typography
              sx={{
                width: '100%',
                textAlign: 'center',
                padding: '14px 0',
              }}
              variant='body1'
              component={'h2'}
            >
              {dictionary.whatNext}
            </Typography>
            <ul
              style={{
                margin: 0,
                padding: isDesktop ? '8px 16px 12px' : '8px 0 12px',
              }}
            >
              <Stack
                component='li'
                sx={{
                  flexDirection: 'row',
                }}
              >
                {listIcon}
                <Typography sx={{ mb: 1 }} variant='body6'>
                  {dictionary.list1}
                </Typography>
              </Stack>
              <Stack
                component='li'
                sx={{
                  flexDirection: 'row',
                }}
              >
                {listIcon}
                <Typography sx={{ mb: 1 }} variant='body6'>
                  {dictionary.list2}
                </Typography>
              </Stack>
              <Stack
                component='li'
                sx={{
                  flexDirection: 'row',
                }}
              >
                {listIcon}
                <Typography sx={{ mb: 1 }} variant='body6'>
                  {dictionary.list3}
                </Typography>
              </Stack>
            </ul>
          </Container>
          <Button
            sx={{
              height: isDesktop ? '46px' : '40px',
              width: isDesktop ? '200px' : '162px',
              fontSize: isDesktop ? '18px' : '16px',
              fontWeight: isDesktop ? 'bold' : '600',
              lineHeight: isDesktop ? '24px' : '26px',
              mt: '40px',
            }}
            variant={'contained'}
            onClick={() =>
              props.link ? navigate(props.link) : navigate('/Equipment')
            }
            endIcon={
              <Avatar
                src={LeftArrowWhite}
                sx={{ width: '16px', height: '16px' }}
              />
            }
          >
            {isDesktop ? dictionary.finish : generalDictionary.finish}
          </Button>
        </Stack>
        <Stack
          sx={{
            m: '24px 0',
          }}
        >
          <ContactUsSegment />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
